import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Context as AppContext } from "../../context/AppContext";
import { enums } from "../../utils/enums-no-imports";
import { getFirestore, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Paper, Button, TextField, Box, Typography, CircularProgress, Snackbar, Alert
} from '@mui/material';
import { MainLayout } from "../../layouts/main-layout/MainLayout";

const StudentRoster = () => {
    const { state: { currentUser }, } = useContext(AppContext);
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [studentCount, setStudentCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    
    // Redirect if not admin or teacher
    if (![enums.ADMIN, enums.TEACHER].includes(currentUser?.role)) {
        navigate('/')    
    }
    
    useEffect(() => {
        fetchStudents();
    }, []);
    
    const fetchStudents = async () => {
        try {
            setLoading(true);
            const db = getFirestore();
            const q = query(
                collection(db, "users"), 
                where("role", "==", enums.STUDENT),
                where("teacherUid", "==", currentUser.uid)
            );
            
            const querySnapshot = await getDocs(q);
            const studentList = [];
            querySnapshot.forEach((doc) => {
                studentList.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            
            setStudents(studentList);
            setStudentCount(studentList.length);
        } catch (error) {
            console.error("Error fetching students:", error);
            setSnackbar({
                open: true,
                message: 'Failed to load students',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };
    
    const addStudent = async (e) => {
        e.preventDefault();
        if (!firstName || !lastName) {
            setSnackbar({
                open: true,
                message: 'Please enter both first and last name',
                severity: 'error'
            });
            return;
        }
        
        try {
            setLoading(true);
            const email = `student${studentCount + 1}@student.com`;
            const password = "student";
            
            // Call the cloud function to create the auth user first
            const functions = getFunctions();
            const createStudentUser = httpsCallable(functions, 'createStudentUser');
            
            const result = await createStudentUser({
                email,
                password,
                firstName,
                lastName,
                teacherUid: currentUser.uid,
                role: enums.STUDENT
            });
            
            if (result.data.success) {
                setSnackbar({
                    open: true,
                    message: `Student added successfully: ${email}`,
                    severity: 'success'
                });
                setFirstName('');
                setLastName('');
                fetchStudents();
            } else {
                throw new Error('Failed to create student account');
            }
        } catch (error) {
            console.error("Error adding student:", error);
            setSnackbar({
                open: true,
                message: `Failed to add student: ${error.message}`,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };
    
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <MainLayout>
            <Box sx={{ padding: '20px' }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Student Roster
                </Typography>
                
                <Box component="form" onSubmit={addStudent} sx={{ mb: 3, display: 'flex', gap: 2 }}>
                    <TextField
                        label="First Name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        size="small"
                    />
                    
                    <TextField
                        label="Last Name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        size="small"
                    />
                    
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        Add Student
                    </Button>
                </Box>
                
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>First Name</TableCell>
                                <TableCell>Last Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Created At</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && students.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : students.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        No students found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                students
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((student) => (
                                        <TableRow key={student.uid}>
                                            <TableCell>{student.firstName}</TableCell>
                                            <TableCell>{student.lastName}</TableCell>
                                            <TableCell>{student.email}</TableCell>
                                            <TableCell>
                                                {student.createdAt ? new Date(student.createdAt).toLocaleDateString() : 'N/A'}
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={students.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
                
                <Snackbar 
                    open={snackbar.open} 
                    autoHideDuration={6000} 
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={handleCloseSnackbar} 
                        severity={snackbar.severity} 
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>  
        </MainLayout>
    );
};

export default StudentRoster;
